import React, { useState, useEffect, useCallback } from 'react';
import { getPropelInfo, getBOMTable, checkJobStatus, getPropelCategories, getCategoryMappings, createItemsBatch, debugPropelJSON, debugBomJSON, exportBomToPropel, exportAttachments, commitImportSession, getFeatureFlags } from '../services/apiService';
import './MainPanel.css';
import ExportAttachmentsModal from './ExportAttachmentsModal';

const MainPanel = ({ selectedElement: activeElement, documentId, workspaceId, isPropelAuthenticated = false, user, elementType: elementTypeProp }) => {
  const [propelInfo, setPropelInfo] = useState({ propelInstanceUrl: '', propelNamespace: '' });
  const [parts, setParts] = useState([]);
  const [loadingParts, setLoadingParts] = useState(false);
  const [partsError, setPartsError] = useState(null);
  
  // Batch Item Creation: Category mappings and modal state
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState('');
  const [, setCategoryMappings] = useState([]);
  const [categoryMappingsLoading, setCategoryMappingsLoading] = useState(false);
  const [categoryMappingsError, setCategoryMappingsError] = useState('');
  const [showItemCreateModal, setShowItemCreateModal] = useState(false);
  const [showExportAttachmentsModal, setShowExportAttachmentsModal] = useState(false);
  const [showMoreActions, setShowMoreActions] = useState(false);
  const [showDebugJsonModal, setShowDebugJsonModal] = useState(false);
  const [debugJsonData, setDebugJsonData] = useState(null);
  const [debugJsonLoading, setDebugJsonLoading] = useState(false);
  const [debugJsonError, setDebugJsonError] = useState(null);
  const [debugJsonTab, setDebugJsonTab] = useState('parts');
  const [debugJsonBomData, setDebugJsonBomData] = useState(null);
  const [debugJsonBomLoading, setDebugJsonBomLoading] = useState(false);
  const [debugJsonBomError, setDebugJsonBomError] = useState(null);
  const [itemCreateRows, setItemCreateRows] = useState([]);
  
  // Job status for item creation
  const [isExporting, setIsExporting] = useState(false);
  const [exportJobId, setExportJobId] = useState(null);
  const [exportJobStatus, setExportJobStatus] = useState(null);
  const [exportJobProgress, setExportJobProgress] = useState(0);
  const [exportJobMessage, setExportJobMessage] = useState('');
  const [exportJobPollingInterval, setExportJobPollingInterval] = useState(null);
  const [showExportResult, setShowExportResult] = useState(false);
  const [bomImportSessionId, setBomImportSessionId] = useState(null);
  const [isCommitting, setIsCommitting] = useState(false);
  const [commitResult, setCommitResult] = useState(null); // 'success' | 'error' | null
  const [exportType, setExportType] = useState(null); // 'items' | 'bom' | 'attachments'
  const [enableStagedImportReview, setEnableStagedImportReview] = useState(false);
  const [selectedImportOption, setSelectedImportOption] = useState('commit'); // 'commit' | 'review'
  
  // Debug mode - controlled by environment or URL parameter
  const [debugMode] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('debug') === 'true' || process.env.NODE_ENV === 'development';
  });

  // Parts Filter State
  const [partsFilter, setPartsFilter] = useState('all');
  const filteredParts = parts.filter(part => {
    if (partsFilter === 'all') return true;
    if (partsFilter === 'inPropel') return !!part.propelItemId;
    if (partsFilter === 'notInPropel') return !part.propelItemId;
    return true;
  });

  /**
   * Fetch BOM data from Onshape
   */
  const fetchBom = useCallback(async () => {
    // Validate all required parameters
    if (!activeElement) {
      console.log('[MainPanel.js] Cannot fetch BOM: activeElement is missing');
      return;
    }
    if (!documentId) {
      console.log('[MainPanel.js] Cannot fetch BOM: documentId is missing');
      return;
    }
    if (!workspaceId) {
      console.log('[MainPanel.js] Cannot fetch BOM: workspaceId is missing');
      return;
    }
    if (!activeElement.id) {
      console.log('[MainPanel.js] Cannot fetch BOM: activeElement.id is missing', activeElement);
      setPartsError('Element ID is missing from selected element');
      return;
    }
    
    console.log(`[MainPanel.js] Fetching BOM for D:${documentId}, W:${workspaceId}, E:${activeElement.id}, Type:${elementTypeProp}`);
    setLoadingParts(true);
    setPartsError(null);
    setParts([]);
    try {
      // Pass elementType to avoid extra API call on backend
      const bomRows = await getBOMTable(documentId, workspaceId, activeElement.id, elementTypeProp);
      console.log('[MainPanel.js] BOM rows received:', bomRows);
      
      // Transform BOMRowDTO objects to UI format
      const bomItems = bomRows.map(row => {
          const item = row.item;
          const isItemInActiveDocument = item.onshapeDocumentId === documentId;

          // Convert additionalBomFields Map to plain object for JSON serialization
          let additionalBomFields = {};
          if (item.additionalBomFields && typeof item.additionalBomFields === 'object') {
            // Handle both Map and plain object
            if (item.additionalBomFields instanceof Map) {
              additionalBomFields = Object.fromEntries(item.additionalBomFields);
            } else {
              additionalBomFields = item.additionalBomFields;
            }
          }

          return {
            id: item.id,
            name: item.name,
            partId: item.partId,
            elementId: item.onshapeElementId,
            documentId: item.onshapeDocumentId,
            wvm: item.wvm,
            wvmId: item.wvmId,
            category: item.category || '',
            onshapeCategory: item.onshapeCategory || item.category || '',
            description: item.description || '',
            thumbnailUrl: item.thumbnailUrl || '',
            partNumber: item.partNumber || '-',
            revision: item.revision || '',
            propelItemId: item.propelItemId || '',
            quantity: row.quantity,
            level: row.level,
            isItemInActiveDocument,
            isTopLevel: row.isTopLevel,
            additionalBomFields
          };
        });
      setParts(bomItems || []);
    } catch (err) {
      console.error('[MainPanel.js] Error fetching BOM:', err);
      setPartsError(err.message || 'Failed to load BOM.');
    } finally {
      setLoadingParts(false);
    }
  }, [activeElement, documentId, workspaceId, elementTypeProp]);

  useEffect(() => {
    fetchBom();
    // Fetch Propel instance info
    getPropelInfo().then(info => setPropelInfo(info)).catch(() => setPropelInfo({ propelInstanceUrl: '', propelNamespace: '' }));
    getFeatureFlags()
      .then(flags => setEnableStagedImportReview(!!flags.enableStagedImportReview))
      .catch(() => setEnableStagedImportReview(false));
  }, [fetchBom]);

  // When flag is OFF and BOM export completes with a staged session, auto-commit
  useEffect(() => {
    if (
      showExportResult &&
      exportType === 'bom' &&
      bomImportSessionId &&
      !enableStagedImportReview &&
      !isCommitting &&
      commitResult === null
    ) {
      handleCommitImportSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showExportResult, exportType, bomImportSessionId, enableStagedImportReview]);

  // Open Item Creation modal and prepare rows + load categories and mappings
  const openItemCreateModal = async () => {
    const missingParts = parts.filter(p => !p.propelItemId && p.isItemInActiveDocument);
    setItemCreateRows(missingParts.map(p => ({
      id: p.id,
      name: p.name,
      onshapeCategory: p.onshapeCategory || p.category || '',
      selectedCategory: '',
      selected: true
    })));
    setShowItemCreateModal(true);

    // Load categories and mappings in parallel
    try {
      setCategoriesLoading(true);
      setCategoryMappingsLoading(true);
      setCategoriesError('');
      setCategoryMappingsError('');
      const [cats, mappings] = await Promise.all([
        getPropelCategories({}),
        getCategoryMappings()
      ]);
      const safeCats = Array.isArray(cats) ? cats : [];
      const safeMappings = Array.isArray(mappings) ? mappings : [];
      setCategories(safeCats);
      setCategoryMappings(safeMappings);

      // Default each row's selectedCategory via mapping if available
      setItemCreateRows(prev => prev.map(r => {
        const m = safeMappings.find(x => (x.onshapeCategory || '').toLowerCase() === (r.onshapeCategory || '').toLowerCase());
        const defaultCat = m ? m.propelCategory : '';
        const exists = safeCats.some(c => (c.name || '').toLowerCase() === (defaultCat || '').toLowerCase());
        return { ...r, selectedCategory: exists ? defaultCat : '', selected: r.selected };
      }));
    } catch (err) {
      console.error('[MainPanel.js] Error loading categories/mappings:', err);
      if (err.needsPropelAuth) {
        setCategoriesError('Propel session expired. Please log out and reconnect to Propel using the button in the header.');
        setCategoryMappingsError('');
      } else {
        setCategoriesError(err.message || 'Failed to load categories');
        setCategoryMappingsError(err.message || 'Failed to load mappings');
      }
    } finally {
      setCategoriesLoading(false);
      setCategoryMappingsLoading(false);
    }
  };

  // Debug handler to preview Propel JSON
  const handleDebugPropelJSON = async () => {
    const selectedRows = itemCreateRows.filter(r => r.selected);
    const invalid = selectedRows.find(r => !r.selectedCategory);
    if (invalid) {
      alert('Please select a Propel Category for all selected parts.');
      return;
    }

    const rows = selectedRows.map(r => {
      const p = parts.find(x => x.id === r.id);
      return {
        id: r.id,
        name: p?.name || r.name,
        description: p?.description || p?.name || r.name || '',
        category: r.selectedCategory,
        documentId: p?.documentId,
        wvm: p?.wvm,
        wvmId: p?.wvmId,
        elementId: p?.elementId,
        partId: p?.partId,
        additionalBomFields: p?.additionalBomFields || {}
      };
    });

    try {
      const resp = await debugPropelJSON(rows);
      if (resp && resp.propelJSON) {
        console.log('=== PROPEL JSON DEBUG ===');
        console.log(JSON.stringify(resp.propelJSON, null, 2));
        console.log('=== END PROPEL JSON DEBUG ===');
        alert('Propel JSON has been logged to the console. Open Developer Tools to view.');
      }
    } catch (err) {
      console.error('[MainPanel.js] Error generating debug Propel JSON:', err);
      alert(`Error generating Propel JSON: ${err.message || 'Unknown error'}`);
    }
  };

  // Standalone Debug JSON — previews Propel JSON for all BOM parts
  const handleStandaloneDebugJSON = () => {
    setShowMoreActions(false);
    setDebugJsonError(null);
    setDebugJsonData(null);
    setDebugJsonBomError(null);
    setDebugJsonBomData(null);
    setDebugJsonTab('parts');
    setShowDebugJsonModal(true);
    setDebugJsonLoading(true);

    const rows = parts.map(p => ({
      id: p.id,
      name: p.name,
      description: p.description || p.name || '',
      category: p.category || p.onshapeCategory || '',
      documentId: p.documentId,
      wvm: p.wvm,
      wvmId: p.wvmId,
      elementId: p.elementId,
      partId: p.partId,
      additionalBomFields: p.additionalBomFields || {}
    }));

    // Fire Part Creation call
    debugPropelJSON(rows)
      .then(resp => setDebugJsonData(resp?.propelJSON ?? resp))
      .catch(err => setDebugJsonError(err.message || 'Failed to generate Part Creation JSON'))
      .finally(() => setDebugJsonLoading(false));

    // Fire BOM Export call only for assemblies
    if (elementTypeProp === 'ASSEMBLY') {
      setDebugJsonBomLoading(true);
      debugBomJSON(parts)
        .then(resp => setDebugJsonBomData(resp?.propelJSON ?? resp))
        .catch(err => setDebugJsonBomError(err.message || 'Failed to generate BOM Export JSON'))
        .finally(() => setDebugJsonBomLoading(false));
    }
  };

  // Create items handler (batch)
  const handleCreateItemsBatch = async () => {
    const selectedRows = itemCreateRows.filter(r => r.selected);
    const invalid = selectedRows.find(r => !r.selectedCategory);
    if (invalid) {
      alert('Please select a Propel Category for all selected parts.');
      return;
    }

    const rows = selectedRows.map(r => {
      const p = parts.find(x => x.id === r.id);
      return {
        id: r.id,
        name: p?.name || r.name,
        description: p?.description || p?.name || r.name || '',
        category: r.selectedCategory,
        documentId: p?.documentId,
        wvm: p?.wvm,
        wvmId: p?.wvmId,
        elementId: p?.elementId,
        partId: p?.partId,
        additionalBomFields: p?.additionalBomFields || {}
      };
    });

    try {
      const resp = await createItemsBatch(rows, activeElement?.name);
      if (resp && resp.jobId) {
        setExportType('items');
        setIsExporting(true);
        setExportJobId(resp.jobId);
        setExportJobStatus('queued');
        setExportJobProgress(0);
        setExportJobMessage('Creating items in Propel and writing back to Onshape...');
        pollJobStatus(resp.jobId);
      } else {
        alert('Batch create started but no jobId returned.');
      }
    } catch (err) {
      console.error('[MainPanel.js] Error starting batch create:', err);
      alert(`Error starting batch create: ${err.message || 'Unknown error'}`);
    }
  };

  // Export BOM to Propel handler
  const handleExportBom = async () => {
    // Check if there are any parts with Propel IDs
    const partsWithPropelIds = parts.filter(p => p.propelItemId);
    
    if (partsWithPropelIds.length === 0) {
      alert('No parts with Propel Item IDs found in the BOM. Please generate part numbers first.');
      return;
    }

    try {
      const resp = await exportBomToPropel(documentId, workspaceId, activeElement.id, activeElement?.name);
      
      if (resp?.jobId) {
        setExportType('bom');
        setIsExporting(true);
        setExportJobId(resp.jobId);
        setExportJobStatus('queued');
        setExportJobProgress(0);
        setExportJobMessage('Exporting BOM to Propel...');
        pollJobStatus(resp.jobId);
      } else {
        alert('BOM export started but no jobId returned.');
      }
    } catch (err) {
      console.error('[MainPanel.js] Error starting BOM export:', err);
      alert(`Error starting BOM export: ${err.message || 'Unknown error'}`);
    }
  };

  // Close export result overlay
  const handleCloseExportResult = () => {
    setIsExporting(false);
    setExportJobId(null);
    setExportJobStatus(null);
    setShowExportResult(false);
    setExportJobMessage('');
    setIsCommitting(false);
    setCommitResult(null);
    setExportType(null);
    setBomImportSessionId(null);
    setSelectedImportOption('commit');
  };

  // Commit a staged Import Session
  const handleCommitImportSession = async () => {
    if (!bomImportSessionId) return;
    setIsCommitting(true);
    setCommitResult(null);
    try {
      await commitImportSession(bomImportSessionId);
      setCommitResult('success');
    } catch (error) {
      console.error('Error committing Import Session:', error);
      setCommitResult('error');
    } finally {
      setIsCommitting(false);
    }
  };

  // Close Item Creation modal; if job succeeded, refresh BOM
  const handleCloseItemCreateModal = () => {
    setShowItemCreateModal(false);
    if (exportJobStatus === 'completed') {
      fetchBom();
    }
    setExportJobStatus(null);
  };

  // Poll job status
  const pollJobStatus = useCallback((jobId) => {
    if (!jobId) return;

    if (exportJobPollingInterval) {
      clearInterval(exportJobPollingInterval);
    }

    const intervalId = setInterval(async () => {
      try {
        const jobStatus = await checkJobStatus(jobId);
        console.log(`[MainPanel.js] Job ${jobId} status:`, jobStatus);
        
        setExportJobStatus(jobStatus.status);
        setExportJobProgress(jobStatus.progress || 0);
        setExportJobMessage(jobStatus.message || '');
        
        if (jobStatus.status === 'completed' || jobStatus.status === 'failed') {
          clearInterval(intervalId);
          setExportJobPollingInterval(null);
          
          if (jobStatus.status === 'completed') {
            setTimeout(() => {
              const warnings = Array.isArray(jobStatus.result?.warnings) ? jobStatus.result.warnings : [];
              const warningsText = warnings.length ? ('\nWarnings:\n' + warnings.map(w => `- ${w}`).join('\n')) : '';
              setExportJobMessage(`Successfully created items in Propel!${jobStatus.result?.message ? '\n' + jobStatus.result.message : ''}${warningsText}`);
              
              if (jobStatus.result && jobStatus.result.importSessionId) {
                setBomImportSessionId(jobStatus.result.importSessionId);
              }
              
              setShowExportResult(true);
              try { fetchBom(); } catch (e) { /* ignore */ }
              setShowItemCreateModal(false);
            }, 1000);
          } else if (jobStatus.status === 'failed') {
            setIsExporting(false);
            setExportJobId(null);
            setExportJobStatus(null);
            alert(`Error creating items: ${jobStatus.error || 'Unknown error'}`);
          }
        }
      } catch (error) {
        console.error('[MainPanel.js] Error polling job status:', error);
        clearInterval(intervalId);
        setExportJobPollingInterval(null);
        setIsExporting(false);
        setExportJobId(null);
        setExportJobStatus(null);
        alert(`Error checking job status: ${error.message || 'Unknown error'}`);
      }
    }, 2000);
    
    setExportJobPollingInterval(intervalId);
    
    return () => clearInterval(intervalId);
  }, [exportJobPollingInterval, fetchBom]);

  const handleExportAttachments = async (selectedItems, formats) => {
    setShowExportAttachmentsModal(false);
    setExportType('attachments');
    setIsExporting(true);
    setExportJobMessage('Initiating attachment export...');
    setExportJobProgress(0);
    
    try {
      const itemsPayload = selectedItems.map(p => ({
        partId: p.partId || p.id,
        elementId: p.elementId || activeElement.id,
        itemName: p.name,
        propelItemId: p.propelItemId,
        propelPartNumber: p.propelPartNumber || p.partNumber,
        category: p.category || '',
        description: p.description || '',
        revision: p.revision || ''
      }));

      const result = await exportAttachments(
        documentId,
        workspaceId,
        activeElement.id,
        itemsPayload,
        formats,
        activeElement?.name
      );
      
      if (result && result.jobId) {
        setExportJobId(result.jobId);
        setExportJobStatus('pending');
        pollJobStatus(result.jobId);
      }
    } catch (error) {
      console.error('Error starting export:', error);
      setIsExporting(false);
      alert(`Failed to start export: ${error.message}`);
    }
  };

  if (!activeElement) {
    console.log('[MainPanel.js] No activeElement, showing empty state. Props:', { activeElement, documentId, workspaceId });
    return (
      <div className="right-panel">
        <div className="empty-state">
          <h3>Propel Integration</h3>
          <p>View BOM and generate part numbers for your assembly</p>
          <div className="info-box">
            <h4>Available Actions:</h4>
            <table className="actions-table">
              <tbody>
                <tr>
                  <td className="action-label">Generate Part Numbers</td>
                  <td className="action-desc">Create Propel Item records for parts in the BOM</td>
                </tr>
                <tr>
                  <td className="action-label">Export BOM to Propel</td>
                  <td className="action-desc">Update BOM structure in Propel (requires parts with Propel IDs)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-panel">
      {isPropelAuthenticated && !loadingParts && parts.length > 0 && (
        <div className="table-actions">
          <button
            className="table-action-button"
            onClick={openItemCreateModal}
            disabled={loadingParts}
          >
            Generate Part Numbers
          </button>
          <div className="more-actions-menu">
            <button
              className="table-action-button more-actions-toggle"
              onClick={() => setShowMoreActions(prev => !prev)}
            >
              More Actions ▾
            </button>
            {showMoreActions && (
              <div className="more-actions-dropdown" onMouseLeave={() => setShowMoreActions(false)}>
                {elementTypeProp === 'ASSEMBLY' && (
                  <button
                    className="dropdown-action-item"
                    onClick={() => { handleExportBom(); setShowMoreActions(false); }}
                    disabled={loadingParts || parts.filter(p => p.propelItemId).length === 0}
                  >
                    Export BOM to Propel
                  </button>
                )}
                <button
                  className="dropdown-action-item"
                  onClick={() => { setShowExportAttachmentsModal(true); setShowMoreActions(false); }}
                  disabled={loadingParts || !parts.some(p => p.propelItemId) || isExporting}
                  title="Export STEP/PDF files to Propel"
                >
                  Export Attachments
                </button>
                <button
                  className="dropdown-action-item"
                  onClick={handleStandaloneDebugJSON}
                  disabled={loadingParts || parts.length === 0}
                  title="Preview the Propel JSON that would be generated"
                >
                  Debug JSON
                </button>
              </div>
            )}
          </div>
          <button
            className="table-action-button refresh-button"
            onClick={fetchBom}
            disabled={loadingParts}
            title="Reload parts from Onshape"
            style={{ marginLeft: 'auto' }}
          >
            Refresh
          </button>
        </div>
      )}

      {/* Item Creation Modal */}
      {showItemCreateModal && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Create Propel Items</h5>
              <button
                type="button"
                className="close-button"
                onClick={() => setShowItemCreateModal(false)}
                aria-label="Close"
              >
                &times;
              </button>
            </div>
            <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {(categoriesLoading || categoryMappingsLoading) ? (
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : (
                <>
                  {(categoryMappingsError || categoriesError) && (
                    <p className="error-text">{categoryMappingsError || categoriesError}</p>
                  )}
                  {itemCreateRows.length === 0 ? (
                    <p className="empty-text">All parts have Propel Item IDs in this assembly.</p>
                  ) : (
                    <>
                      {(() => {
                        const selectedCount = itemCreateRows.filter(r => r.selected).length;
                        const allSelected = selectedCount === itemCreateRows.length;
                        const noneSelected = selectedCount === 0;
                        return (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.9rem' }}>
                              <input
                                type="checkbox"
                                checked={allSelected}
                                ref={el => { if (el) el.indeterminate = !allSelected && !noneSelected; }}
                                onChange={(e) => setItemCreateRows(prev => prev.map(r => ({ ...r, selected: e.target.checked })))}
                              />
                              <strong>Select All</strong>
                            </label>
                            <span style={{ color: '#555', fontSize: '0.85rem' }}>
                              {selectedCount} of {itemCreateRows.length} selected
                            </span>
                          </div>
                        );
                      })()}
                      <table className="parts-table">
                        <thead>
                          <tr>
                            <th style={{ width: '32px' }}></th>
                            <th>Part</th>
                            <th>Propel Category</th>
                          </tr>
                        </thead>
                        <tbody>
                          {itemCreateRows.map((row, idx) => (
                            <tr key={row.id} style={!row.selected ? { opacity: 0.45 } : {}}>
                              <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                <input
                                  type="checkbox"
                                  checked={row.selected}
                                  onChange={(e) => setItemCreateRows(prev => prev.map((r, i) => i === idx ? { ...r, selected: e.target.checked } : r))}
                                />
                              </td>
                              <td>
                                <div className="part-cell">
                                  <span className="part-name">{row.name}</span>
                                  <small>
                                    {(row.onshapeCategory && row.onshapeCategory.trim()) ? `Onshape: ${row.onshapeCategory}` : 'Onshape: N/A'}
                                  </small>
                                </div>
                              </td>
                              <td>
                                <select
                                  value={row.selectedCategory}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setItemCreateRows(prev => prev.map((r, i) => i === idx ? { ...r, selectedCategory: value } : r));
                                  }}
                                  disabled={!row.selected}
                                  style={{ width: '100%' }}
                                >
                                  <option value="">Select a category</option>
                                  {categories.map(cat => (
                                    <option key={cat.id} value={cat.name} disabled={!!cat.disabled}>
                                      {cat.name}
                                    </option>
                                  ))}
                                </select>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                  <div className="mt-3" style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                    <div>
                      {debugMode && (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleDebugPropelJSON}
                          disabled={itemCreateRows.filter(r => r.selected).length === 0}
                          style={{ backgroundColor: '#6c757d', color: 'white' }}
                        >
                          Get Propel JSON
                        </button>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleCloseItemCreateModal}
                      >
                        Close
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleCreateItemsBatch}
                        disabled={itemCreateRows.filter(r => r.selected).length === 0}
                        autoFocus
                      >
                        Generate
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {loadingParts && <p className="loading-text">Loading parts...</p>}
      {partsError && <p className="error-text">{partsError}</p>}
      {!loadingParts && !partsError && parts.length === 0 && (
        <p className="empty-text">No parts found in this element.</p>
      )}
      {!loadingParts && !partsError && parts.length > 0 && (
        <div className="parts-filter-container" style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
          <label htmlFor="parts-filter" style={{ marginRight: '8px' }}>Filter:</label>
          <select
            id="parts-filter"
            value={partsFilter}
            onChange={e => setPartsFilter(e.target.value)}
            style={{ marginRight: '8px' }}
          >
            <option value="all">All</option>
            <option value="inPropel">In Propel</option>
            <option value="notInPropel">Not in Propel</option>
          </select>
          <span style={{ color: '#888', fontSize: '0.93em', marginLeft: '10px' }}>{filteredParts.length} row{filteredParts.length === 1 ? '' : 's'} matched</span>
        </div>
      )}

      {!loadingParts && !partsError && filteredParts.length > 0 && (
        <table className="parts-table">
          <thead>
            <tr>
              <th>Part Number</th>
              <th>Part Name</th>
              <th>Quantity</th>
            </tr>
          </thead>
          <tbody>
            {filteredParts.map(part => (
              <tr key={part.id} className={part.isTopLevel ? 'top-level-row' : ''}>
                <td>
                  {part.propelItemId && propelInfo.propelInstanceUrl && propelInfo.propelNamespace ? (
                    <a 
                      href={`${propelInfo.propelInstanceUrl}/lightning/r/${propelInfo.propelNamespace}__Item__c/${part.propelItemId}/view`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="propel-link"
                    >
                      {part.partNumber || <span className="not-available">N/A</span>}
                      <span 
                        className="external-link-icon tooltip" 
                        aria-hidden="true"
                        data-tooltip="Opens in Propel PLM"
                      > ↗</span>
                      <span className="sr-only">(opens in new window)</span>
                    </a>
                  ) : (
                    part.partNumber || <span className="not-available">N/A</span>
                  )}
                </td>
                <td>
                  <span style={{ marginLeft: `${(part.level != null ? (String(part.level).includes('.') ? String(part.level).split('.').length - 1 : Number(part.level) || 0) : 0) * 1.5}em` }}>
                    {part.name}
                  </span>
                </td>
                <td>{part.quantity}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Item Creation Job Progress Modal */}
      {isExporting && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {exportType === 'bom' ? 'Exporting BOM' : exportType === 'attachments' ? 'Exporting Attachments' : 'Creating Items'}
              </h5>
              {showExportResult && (
                <button 
                  type="button" 
                  className="close-button" 
                  onClick={handleCloseExportResult}
                  aria-label="Close"
                >
                  &times;
                </button>
              )}
            </div>
            <div className="modal-body">
              {!showExportResult ? (
                <>
                  {exportJobProgress > 0 ? (
                    <div className="progress mb-3">
                      <div 
                        className="progress-bar" 
                        role="progressbar" 
                        style={{ width: `${exportJobProgress}%` }} 
                        aria-valuenow={exportJobProgress} 
                        aria-valuemin="0" 
                        aria-valuemax="100"
                      >
                        {exportJobProgress}%
                      </div>
                    </div>
                  ) : (
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  )}
                  <p className="mt-3">
                    {exportJobMessage || 'Creating items in Propel. This may take a few minutes...'}
                  </p>
                  {exportJobId && exportJobStatus && (
                    <p className="text-muted small">Job ID: {exportJobId} | Status: {exportJobStatus}</p>
                  )}
                </>
              ) : (
                <>
                  {/* FLAG OFF: auto-commit flow — show spinner while committing, then result */}
                  {!enableStagedImportReview && exportType === 'bom' && bomImportSessionId ? (
                    <>
                      {commitResult === null && (
                        <div className="spinner-border" role="status">
                          <span className="visually-hidden">Importing...</span>
                        </div>
                      )}
                      {commitResult === 'success' && (
                        <div className="alert alert-success mb-3">
                          <p className="mb-0">BOM has been imported to Propel.</p>
                        </div>
                      )}
                      {commitResult === 'error' && (
                        <div className="alert alert-danger mb-3">
                          <p className="mb-0">Failed to import. Please import manually from Propel.</p>
                        </div>
                      )}
                      <div className="staged-footer">
                        <button type="button" className="btn-link" onClick={handleCloseExportResult}>Close</button>
                      </div>
                    </>
                  ) : exportType === 'items' ? (
                    <>
                      <div className="alert alert-success mb-3">
                        <p className="mb-0">Part numbers created successfully in Propel.</p>
                      </div>
                      <div className="staged-footer">
                        <button type="button" className="btn-link" onClick={handleCloseExportResult}>Close</button>
                      </div>
                    </>
                  ) : exportType === 'attachments' ? (
                    <>
                      <div className="alert alert-success mb-3">
                        <p className="mb-0">Attachments have been exported successfully to Propel.</p>
                      </div>
                      <div className="staged-footer">
                        <button type="button" className="btn-link" onClick={handleCloseExportResult}>Close</button>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Success/error banners */}
                      {commitResult === 'success' ? (
                        <div className="alert alert-success mb-3">
                          <p className="mb-0">BOM has been imported successfully in Propel.</p>
                        </div>
                      ) : commitResult === 'error' ? (
                        <div className="alert alert-danger mb-3">
                          <p className="mb-0">Failed to import. Please import manually from Propel.</p>
                        </div>
                      ) : (
                        <div className="alert alert-success mb-3">
                          <p className="mb-0">BOM has been staged successfully in Propel.</p>
                        </div>
                      )}

                      {/* FLAG ON: radio button UI */}
                      {enableStagedImportReview && bomImportSessionId && propelInfo?.propelInstanceUrl && propelInfo?.propelNamespace && commitResult == null && (
                        <div className="import-option-group">
                          <label className="import-option">
                            <input
                              type="radio"
                              name="importOption"
                              value="commit"
                              checked={selectedImportOption === 'commit'}
                              onChange={() => setSelectedImportOption('commit')}
                            />
                            <span className="import-option-label">
                              <span className="import-option-title">Import without Review</span>
                              <span className="import-option-hint">Immediately import the BOM into Propel. No further review needed.</span>
                            </span>
                          </label>
                          <label className="import-option">
                            <input
                              type="radio"
                              name="importOption"
                              value="review"
                              checked={selectedImportOption === 'review'}
                              onChange={() => setSelectedImportOption('review')}
                            />
                            <span className="import-option-label">
                              <span className="import-option-title">Review before Importing</span>
                              <span className="import-option-hint">Open the staged BOM in Propel to review changes before they are imported.</span>
                            </span>
                          </label>
                        </div>
                      )}

                      <div className="staged-footer">
                        {enableStagedImportReview && bomImportSessionId && commitResult == null && (
                          <button
                            type="button"
                            className="btn btn-primary"
                            disabled={isCommitting}
                            onClick={() => {
                              if (selectedImportOption === 'commit') {
                                handleCommitImportSession();
                              } else {
                                window.open(
                                  `${propelInfo.propelInstanceUrl}/lightning/r/${propelInfo.propelNamespace}__Import_Session__c/${bomImportSessionId}/view`,
                                  '_blank',
                                  'noopener,noreferrer'
                                );
                              }
                            }}
                          >
                            {isCommitting ? <><span className="spinner-border spinner-border-sm"></span>{' '}Importing...</> : 'Proceed'}
                          </button>
                        )}
                        <button type="button" className="btn-link" onClick={handleCloseExportResult}>Close</button>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      <ExportAttachmentsModal
        isOpen={showExportAttachmentsModal}
        onClose={() => setShowExportAttachmentsModal(false)}
        parts={parts.filter(p => p.propelItemId)}
        onExport={handleExportAttachments}
      />

      {/* Debug JSON Modal */}
      {showDebugJsonModal && (
        <div className="modal-backdrop">
          <div className="modal-content modal-content-wide">
            <div className="modal-header">
              <h5 className="modal-title">Propel JSON Preview</h5>
              <button
                type="button"
                className="close-button"
                onClick={() => setShowDebugJsonModal(false)}
                aria-label="Close"
              >
                &times;
              </button>
            </div>
            <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {/* Tab bar */}
              <div style={{ display: 'flex', gap: '4px', marginBottom: '12px', borderBottom: '1px solid #dee2e6' }}>
                <button
                  type="button"
                  style={{
                    padding: '6px 16px',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    borderBottom: debugJsonTab === 'parts' ? '2px solid #0d6efd' : '2px solid transparent',
                    fontWeight: debugJsonTab === 'parts' ? '600' : '400',
                    color: debugJsonTab === 'parts' ? '#0d6efd' : '#6c757d'
                  }}
                  onClick={() => setDebugJsonTab('parts')}
                >
                  Part Creation
                </button>
                {elementTypeProp === 'ASSEMBLY' && (
                  <button
                    type="button"
                    style={{
                      padding: '6px 16px',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      borderBottom: debugJsonTab === 'bom' ? '2px solid #0d6efd' : '2px solid transparent',
                      fontWeight: debugJsonTab === 'bom' ? '600' : '400',
                      color: debugJsonTab === 'bom' ? '#0d6efd' : '#6c757d'
                    }}
                    onClick={() => setDebugJsonTab('bom')}
                  >
                    BOM Export
                  </button>
                )}
              </div>

              {/* Part Creation tab content */}
              {debugJsonTab === 'parts' && (
                <>
                  {debugJsonLoading && (
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  )}
                  {debugJsonError && (
                    <p className="error-text">{debugJsonError}</p>
                  )}
                  {!debugJsonLoading && !debugJsonError && debugJsonData && (
                    <pre className="debug-json-output">{JSON.stringify(debugJsonData, null, 2)}</pre>
                  )}
                </>
              )}

              {/* BOM Export tab content */}
              {debugJsonTab === 'bom' && elementTypeProp === 'ASSEMBLY' && (
                <>
                  {debugJsonBomLoading && (
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  )}
                  {debugJsonBomError && (
                    <p className="error-text">{debugJsonBomError}</p>
                  )}
                  {!debugJsonBomLoading && !debugJsonBomError && debugJsonBomData && (
                    <pre className="debug-json-output">{JSON.stringify(debugJsonBomData, null, 2)}</pre>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              {debugJsonTab === 'parts' && !debugJsonLoading && !debugJsonError && debugJsonData && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => navigator.clipboard.writeText(JSON.stringify(debugJsonData, null, 2))}
                >
                  Copy JSON
                </button>
              )}
              {debugJsonTab === 'bom' && !debugJsonBomLoading && !debugJsonBomError && debugJsonBomData && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => navigator.clipboard.writeText(JSON.stringify(debugJsonBomData, null, 2))}
                >
                  Copy JSON
                </button>
              )}
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowDebugJsonModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainPanel;
